import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AlertTriangle,
    Check,
    ChevronRight,
    Clock,
    Edit3,
    Loader,
    Plus,
    Sparkles,
    UserPlus,
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import {
    generateInsightFromNLP,
    generateQueriesFromInsights,
    generateQueryForInsight,
} from '../../services/agentApi';
import {
    IMPLEMENTATION_STATUS,
    upsertImplementation,
} from '../../utils/reportPlanImplementations';
import {
    QueryRefinementModal,
    buildRefinementInstruction,
} from '../queries/QueryRefinementModal';
import { Card } from '../ui/Card';

const TYPE_META = {
    refine_query: {
        labelKey: 'typeLabels.refineQuery',
        icon: Edit3,
        badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
    },
    add_query: {
        labelKey: 'typeLabels.addQuery',
        icon: Plus,
        badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
    },
    new_insight: {
        labelKey: 'typeLabels.newInsight',
        icon: Sparkles,
        badgeClass: 'bg-violet-100 text-violet-800 border-violet-200',
    },
    new_sub_agent: {
        labelKey: 'typeLabels.newSubAgent',
        icon: UserPlus,
        badgeClass: 'bg-orange-100 text-orange-800 border-orange-200',
    },
};

function buildSubAgentMarkdown(rec, t) {
    const tables = (rec.tables || []).map((tbl) => `- ${tbl}`).join('\n');
    return [
        `# ${rec.sub_agent_name || t('typeLabels.newSubAgent')}`,
        '',
        `## ${t('subAgentMarkdown.mission')}`,
        rec.description || '',
        '',
        `## ${t('subAgentMarkdown.domainFocus')}`,
        rec.focus || '',
        '',
        `## ${t('subAgentMarkdown.tablesYouMayUse')}`,
        tables || `- ${t('subAgentMarkdown.specifyInAgentSettings')}`,
    ].join('\n');
}

function buildReportPlanContext(reportGenerationId, databaseId, index, recommendation) {
    if (!reportGenerationId || !databaseId) return null;
    return { reportGenerationId, databaseId, recommendationIndex: index, recommendation };
}

export function ReportRecommendations({
    recommendations = [],
    databaseId,
    database,
    reportGenerationId,
    databaseQueriesMap = {},
    implementations = [],
    onImplementationsChange,
}) {
    const navigate = useNavigate();
    const { t } = useTranslation('report-panels');
    const { showToast } = useToast();
    const [busyKey, setBusyKey] = useState(null);
    const [refinementModal, setRefinementModal] = useState(null);

    if (!recommendations.length) return null;

    const persist = (index, recommendation, outcome) => {
        if (!reportGenerationId || !onImplementationsChange) return;
        onImplementationsChange(
            upsertImplementation(reportGenerationId, index, recommendation, outcome)
        );
    };

    const getImpl = (idx) => implementations.find((item) => item.index === idx);

    const run = async (key, fn) => {
        setBusyKey(key);
        try {
            await fn();
        } finally {
            setBusyKey(null);
        }
    };

    const openRefinementModal = (rec, idx) => {
        const query = databaseQueriesMap[rec.query_id];
        if (!query) {
            showToast(t('recommendations.toastQueryNotFound'), 'error');
            return;
        }
        const impl = getImpl(idx);
        if (impl?.outcome?.status !== IMPLEMENTATION_STATUS.COMPLETED) {
            persist(idx, rec, {
                status: IMPLEMENTATION_STATUS.IN_PROGRESS,
                phase: 'refine_query',
                query_id: rec.query_id,
            });
        }
        setRefinementModal({
            rec,
            idx,
            query,
            instruction: buildRefinementInstruction(rec),
            reportPlanContext: buildReportPlanContext(
                reportGenerationId,
                databaseId,
                idx,
                rec
            ),
        });
    };

    const closeRefinementModal = () => setRefinementModal(null);

    const handleRefinementAccepted = () => {
        if (!refinementModal || !onImplementationsChange || !reportGenerationId) return;
        onImplementationsChange(
            upsertImplementation(
                reportGenerationId,
                refinementModal.idx,
                refinementModal.rec,
                {
                    status: IMPLEMENTATION_STATUS.COMPLETED,
                    query_id: refinementModal.rec.query_id,
                    insight_id: refinementModal.rec.insight_id,
                }
            )
        );
        closeRefinementModal();
    };

    const handleAddQuery = (rec, idx) => {
        run(`add-${idx}`, async () => {
            const res = await generateQueryForInsight(rec.insight_id, rec.instruction);
            if (res.data?.status === 'success') {
                const generatedQueryId =
                    res.data?.data?.generated_query_id ?? res.data?.data?.query?.id;
                persist(idx, rec, {
                    status: IMPLEMENTATION_STATUS.COMPLETED,
                    insight_id: rec.insight_id,
                    generated_query_id: generatedQueryId,
                });
                showToast(t('recommendations.toastQueryGenerated'), 'success');
            } else {
                showToast(res.data?.message || t('recommendations.toastFailedGenerateQuery'), 'error');
            }
        });
    };

    const handleNewInsight = (rec, idx) => {
        run(`insight-${idx}`, async () => {
            const prompt = [rec.insight_name, rec.insight_description]
                .filter(Boolean)
                .join('. ');
            const res = await generateInsightFromNLP(rec.sub_agent_id, prompt);
            if (res.data?.status !== 'success') {
                showToast(res.data?.message || t('recommendations.toastFailedCreateInsight'), 'error');
                return;
            }
            const insightId = res.data.data?.insight?.id;
            if (!insightId) {
                showToast(t('recommendations.toastInsightCreatedIdMissing'), 'warning');
                return;
            }
            const generatedQueryIds = [];
            for (const q of rec.queries_to_add || []) {
                if (!q.instruction?.trim()) continue;
                const qRes = await generateQueryForInsight(insightId, q.instruction);
                if (qRes.data?.status === 'success') {
                    const qid =
                        qRes.data?.data?.generated_query_id ?? qRes.data?.data?.query?.id;
                    if (qid) generatedQueryIds.push(qid);
                }
            }
            if ((rec.queries_to_add || []).length > 0) {
                await generateQueriesFromInsights([insightId]);
            }
            persist(idx, rec, {
                status: IMPLEMENTATION_STATUS.COMPLETED,
                insight_id: insightId,
                generated_query_ids: generatedQueryIds,
            });
            showToast(t('recommendations.toastInsightAndQueriesCreated'), 'success');
        });
    };

    const handleNewSubAgent = (rec, idx) => {
        persist(idx, rec, {
            status: IMPLEMENTATION_STATUS.IN_PROGRESS,
            phase: 'new_sub_agent',
            sub_agent_name: rec.sub_agent_name,
        });
        navigate('/agents/sub-agent/create', {
            state: {
                database,
                prefillName: rec.sub_agent_name,
                prefillMarkdown: buildSubAgentMarkdown(rec, t),
                reportPlanContext: buildReportPlanContext(
                    reportGenerationId,
                    databaseId,
                    idx,
                    rec
                ),
            },
        });
    };

    const renderAction = (rec, idx) => {
        const impl = getImpl(idx);
        const isCompleted = impl?.outcome?.status === IMPLEMENTATION_STATUS.COMPLETED;
        const isInProgress = impl?.outcome?.status === IMPLEMENTATION_STATUS.IN_PROGRESS;

        if (isCompleted) {
            return (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700">
                    <Check className="w-4 h-4" />
                    {t('recommendations.doneReadyForPlanUpdate')}
                </span>
            );
        }
        if (isInProgress && rec.type === 'refine_query') {
            return (
                <button
                    type="button"
                    onClick={() => openRefinementModal(rec, idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                >
                    <Clock className="w-4 h-4" />
                    {t('recommendations.continueRefinement')}
                    <ChevronRight className="w-4 h-4" />
                </button>
            );
        }
        if (isInProgress) {
            return (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
                    <Clock className="w-4 h-4" />
                    {t('recommendations.inProgressFinishAction')}
                </span>
            );
        }

        const busy = busyKey === `add-${idx}` || busyKey === `insight-${idx}`;

        if (rec.type === 'refine_query') {
            return (
                <button
                    type="button"
                    onClick={() => openRefinementModal(rec, idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                >
                    {t('recommendations.refineQuery')}
                    <ChevronRight className="w-4 h-4" />
                </button>
            );
        }
        if (rec.type === 'add_query') {
            return (
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleAddQuery(rec, idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                    {busy ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {t('recommendations.generateQuery')}
                </button>
            );
        }
        if (rec.type === 'new_insight') {
            return (
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleNewInsight(rec, idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                >
                    {busy ? <Loader className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {t('recommendations.createInsightAndQueries')}
                </button>
            );
        }
        if (rec.type === 'new_sub_agent') {
            return (
                <button
                    type="button"
                    onClick={() => handleNewSubAgent(rec, idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700"
                >
                    <UserPlus className="w-4 h-4" />
                    {t('recommendations.createSubAgent')}
                    <ChevronRight className="w-4 h-4" />
                </button>
            );
        }
        return null;
    };

    return (
        <Card padding="md" className="rounded-xl mb-6">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('recommendations.title')}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {t('recommendations.subtitle')}
                    </p>
                </div>
            </div>
            <div className="space-y-4">
                {recommendations.map((rec, idx) => {
                    const meta = TYPE_META[rec.type] || TYPE_META.add_query;
                    const Icon = meta.icon;
                    const impl = getImpl(idx);
                    const isCompleted = impl?.outcome?.status === IMPLEMENTATION_STATUS.COMPLETED;
                    const isInProgress = impl?.outcome?.status === IMPLEMENTATION_STATUS.IN_PROGRESS;
                    return (
                        <Card
                            key={`${rec.type}-${idx}`}
                            padding="base"
                            className={`shadow-none ${
                                isCompleted
                                    ? 'border-green-200 bg-green-50/40'
                                    : isInProgress
                                        ? 'border-amber-200 bg-amber-50/40'
                                        : 'border-orange-100 bg-orange-50/40'
                            }`}
                        >
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${meta.badgeClass}`}>
                                    <Icon className="w-3.5 h-3.5" />
                                    {t(meta.labelKey)}
                                </span>
                                {rec.sub_agent_name && (
                                    <span className="text-xs text-gray-500">{rec.sub_agent_name}</span>
                                )}
                            </div>
                            {rec.blocking_reason && (
                                <p className="text-sm text-gray-700 mb-2">{rec.blocking_reason}</p>
                            )}
                            {rec.type === 'refine_query' && (
                                <p className="text-sm text-gray-600 mb-2">
                                    <span className="font-medium">{rec.query_name || t('recommendations.queryFallback', { id: rec.query_id })}</span>
                                    {' — '}
                                    {rec.instruction}
                                </p>
                            )}
                            {rec.type === 'add_query' && (
                                <p className="text-sm text-gray-600 mb-2">
                                    <span className="font-medium">{rec.insight_name}</span>
                                    {' — '}
                                    {rec.instruction}
                                </p>
                            )}
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                {renderAction(rec, idx)}
                            </div>
                        </Card>
                    );
                })}
            </div>

            {refinementModal && (
                <QueryRefinementModal
                    open
                    onClose={closeRefinementModal}
                    query={refinementModal.query}
                    queryId={refinementModal.rec.query_id}
                    databaseId={databaseId}
                    initialInstruction={refinementModal.instruction}
                    guided
                    reportPlanContext={refinementModal.reportPlanContext}
                    onAccepted={handleRefinementAccepted}
                />
            )}
        </Card>
    );
}
